import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { Search, UserCheck, UserX, Shield, Store, User } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_ICONS = { admin: Shield, seller: Store, customer: User };
const ROLE_COLORS = { admin: { bg: '#fee2e2', text: '#dc2626' }, seller: { bg: '#ede9fe', text: '#7c3aed' }, customer: { bg: '#dbeafe', text: '#2563eb' } };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [toggling, setToggling] = useState(null);
  const limit = 12;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUsers({ page, limit, search, role: roleFilter });
      setUsers(res.data.users || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [page, search, roleFilter]);

  const handleToggle = async (userId, currentStatus) => {
    setToggling(userId);
    try {
      await adminAPI.toggleUserStatus(userId);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setToggling(null);
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Users</h1>
        <p style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{total} users registered</p>
      </div>

      {/* Role summary */}
      <div style={{ display: 'flex', gap: 12 }}>
        {['', 'admin', 'seller', 'customer'].map(r => {
          const label = r ? r.charAt(0).toUpperCase() + r.slice(1) : 'All';
          const active = roleFilter === r;
          return (
            <button key={r} onClick={() => { setRoleFilter(r); setPage(1); }} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', background: active ? '#f43397' : 'white', color: active ? 'white' : '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ background: 'white', borderRadius: 12, padding: '10px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Search size={16} color="#94a3b8" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or email..." style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1 }} />
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['User', 'Email', 'Phone', 'Role', 'Joined', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No users found</td></tr>
                ) : users.map(u => {
                  const rc = ROLE_COLORS[u.role] || { bg: '#f1f5f9', text: '#64748b' };
                  const RoleIcon = ROLE_ICONS[u.role] || User;
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, #f43397, #6c3fc5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 15 }}>
                            {u.name?.[0]?.toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{u.email}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{u.phone || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: rc.bg, color: rc.text, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          <RoleIcon size={12} />
                          {u.role}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>
                        {new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: u.is_active ? '#dcfce7' : '#fee2e2', color: u.is_active ? '#16a34a' : '#dc2626', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {u.role !== 'admin' && (
                          <button
                            onClick={() => handleToggle(u.id, u.is_active)}
                            disabled={toggling === u.id}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: 'none', borderRadius: 8, background: u.is_active ? '#fee2e2' : '#dcfce7', color: u.is_active ? '#dc2626' : '#16a34a', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {toggling === u.id ? '...' : u.is_active ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Activate</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6, justifyContent: 'center' }}>
            {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: page === p ? '#f43397' : '#f1f5f9', color: page === p ? 'white' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
